import { diaNegocio, sumarDias } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Palmtree, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { useBarberos, useConfig } from '../agenda/datos';
import { avisarError } from '@/lib/avisos';

interface Vacacion {
  id: string;
  barberoId: string;
  barbero: string;
  desde: string;
  hasta: string;
  motivo: string | null;
  estado: 'solicitada' | 'autorizada' | 'rechazada';
}

const TONO = { solicitada: 'aviso', autorizada: 'exito', rechazada: 'neutro' } as const;

export function VacacionesPage() {
  const { usuario, puede } = useSesion();
  const qc = useQueryClient();
  const barberos = useBarberos();
  const anticipacion = useConfig<number>('vacaciones.anticipacionDias', 30);
  const [nueva, setNueva] = useState(false);
  const hoy = diaNegocio(new Date());
  const minimo = puede('personal.vacaciones_autorizar') ? hoy : sumarDias(hoy, anticipacion);
  const [form, setForm] = useState({ barberoId: '', desde: minimo, hasta: minimo, motivo: '' });

  const lista = useQuery({ queryKey: ['vacaciones'], queryFn: () => api<Vacacion[]>('/vacaciones') });
  const solicitar = useMutation({
    mutationFn: () =>
      api('/vacaciones', {
        method: 'POST',
        body: { barberoId: form.barberoId || undefined, desde: form.desde, hasta: form.hasta, motivo: form.motivo || undefined },
      }),
    onSuccess: () => {
      toast.success('Solicitud registrada');
      setNueva(false);
      void qc.invalidateQueries({ queryKey: ['vacaciones'] });
    },
    onError: (e) => avisarError(e),
  });
  const resolver = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: 'autorizada' | 'rechazada' }) => api(`/vacaciones/${id}`, { method: 'PATCH', body: { estado } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vacaciones'] });
      void qc.invalidateQueries({ queryKey: ['horarios'] });
    },
    onError: (e) => avisarError(e),
  });

  return (
    <ModuleLayout titulo="Personal" secciones={SECCIONES_POR_MODULO.personal}>
      <EncabezadoPagina
        titulo="Vacaciones"
        descripcion={`Se solicitan con al menos ${anticipacion} días de anticipación y las autoriza el dueño.`}
        acciones={
          <Button onClick={() => setNueva(true)}>
            <Plus /> Solicitar vacaciones
          </Button>
        }
      />
      <Tarjeta>
        {lista.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !lista.data?.length ? (
          <Vacio icono={<Palmtree />} titulo="Sin solicitudes" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Barbero</Th>
                <Th>Del</Th>
                <Th>Al</Th>
                <Th>Motivo</Th>
                <Th>Estado</Th>
                <Th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {lista.data.map((v) => (
                <Tr key={v.id}>
                  <Td className="font-medium">{v.barbero}</Td>
                  <Td className="cifras">{v.desde}</Td>
                  <Td className="cifras">{v.hasta}</Td>
                  <Td className="text-foreground-muted">{v.motivo ?? '—'}</Td>
                  <Td>
                    <Insignia tono={TONO[v.estado]}>{v.estado === 'solicitada' ? 'Por autorizar' : v.estado === 'autorizada' ? 'Autorizada' : 'Rechazada'}</Insignia>
                  </Td>
                  <Td>
                    {v.estado === 'solicitada' && puede('personal.vacaciones_autorizar') && (
                      <div className="flex gap-1">
                        <Button tamano="icono" variante="secundario" aria-label="Autorizar" onClick={() => resolver.mutate({ id: v.id, estado: 'autorizada' })}>
                          <Check />
                        </Button>
                        <Button tamano="icono" variante="fantasma" aria-label="Rechazar" onClick={() => resolver.mutate({ id: v.id, estado: 'rechazada' })}>
                          <X />
                        </Button>
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>

      {nueva && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={setNueva}
          titulo="Solicitar vacaciones"
          pie={
            <>
              <Button variante="secundario" onClick={() => setNueva(false)}>
                Cancelar
              </Button>
              <Button onClick={() => solicitar.mutate()} cargando={solicitar.isPending}>
                Enviar solicitud
              </Button>
            </>
          }
        >
          <div className="grid gap-3">
            {puede('personal.horarios') && (
              <Campo etiqueta="Barbero">
                {(p) => (
                  <Select {...p} value={form.barberoId} onChange={(e) => setForm({ ...form, barberoId: e.target.value })}>
                    <option value="">Yo ({usuario?.nombre})</option>
                    {barberos.data
                      ?.filter((b) => b.id !== usuario?.id)
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.nombre}
                        </option>
                      ))}
                  </Select>
                )}
              </Campo>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Campo etiqueta="Desde">{(p) => <Input {...p} type="date" min={minimo} value={form.desde} onChange={(e) => setForm({ ...form, desde: e.target.value })} />}</Campo>
              <Campo etiqueta="Hasta">{(p) => <Input {...p} type="date" min={form.desde} value={form.hasta} onChange={(e) => setForm({ ...form, hasta: e.target.value })} />}</Campo>
            </div>
            <Campo etiqueta="Motivo (opcional)">{(p) => <Input {...p} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />}</Campo>
          </div>
        </Dialogo>
      )}
    </ModuleLayout>
  );
}
